import { ref, watch } from 'vue-demi';

/**
 * The purpose of this composable is to improve UX by
 * preventing loaders (e.g. spinners, skeletons etc) from flashing on screen for a few milliseconds
 * (for example a HTTP request completes in under 200 ms)
 *
 * It works as follows:
 * - if loading for less than loaderDelayMs do not show loader
 * - if loader does show - show for at least loaderMinTimeVisibleMs.
 *
 * @param isLoading {Ref<UnwrapRef<boolean>>} - reactive boolean loading state. default is 200ms
 * @param loaderDelayMs - how long should wait before showing loader in milliseconds
 * @param loaderMinTimeVisibleMs - min time loader should show (if it does show) in milliseconds. default is 500ms
 * @returns {{shouldShowLoader: Ref<UnwrapRef<boolean>>}}
 */
export default function usePreventLoaderFlash(isLoading, loaderDelayMs = 200, loaderMinTimeVisibleMs = 500) {
  const shouldShowLoader = ref(false);
  const loaderMinTimeVisibleExceeded = ref(true);

  let delayShowLoaderTimeoutId;
  let delayHideLoaderTimeoutId;

  // hide loader and clear timers
  function hideLoader() {
    clearTimeout(delayShowLoaderTimeoutId);
    clearTimeout(delayHideLoaderTimeoutId);
    shouldShowLoader.value = false;
  }
  // show loader and start timer to persist loader for
  // at least SKELETON_MIN_TIME_VISIBLE_MS
  function showLoader() {
    shouldShowLoader.value = true;
    loaderMinTimeVisibleExceeded.value = false;
    // ensure loader shows for a minimum of 500ms
    delayHideLoaderTimeoutId = setTimeout(() => {
      loaderMinTimeVisibleExceeded.value = true;
      // if still loading dont hide loader
      if (!isLoading.value) {
        hideLoader();
      }
    }, loaderMinTimeVisibleMs);
  }

  function delayLoader(isLoadingValue) {
    if (!shouldShowLoader.value && isLoadingValue) {
      delayShowLoaderTimeoutId = setTimeout(() => {
        showLoader();
      }, loaderDelayMs);
    }

    // hide loader if no longer loading and loader has exceeded its minimum time to be visible.
    if (!isLoadingValue && loaderMinTimeVisibleExceeded.value) {
      hideLoader();
    }
  }

  watch(isLoading, delayLoader, {
    immediate: true,
  });
  return {
    shouldShowLoader,
  };
}
